<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  BuildingOffice2Icon,
  MapPinIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'

interface EligibilityOffer {
  zone: string
  status: string
  offerId: string
  provider: string
  offerName: string
  priceEuros: {
    recurrent: string
    serviceAccessFees: string
  } | null
  technology: string
  bandwidthMbps: string
  productFamily: string
  commitmentMonths: string
}

interface GeneralInfo {
  name: string
  zone: Record<string, Record<string, { key: string; value: string }>>
}

interface EligibilityResult {
  gps?: {
    latitude: string
    longitude: string
  }
  address?: string
  buildingCode?: string
  eligibilityOffers?: EligibilityOffer[]
  generalInformation?: GeneralInfo[]
}

interface Props {
  result: EligibilityResult | null
  fulladdress?: string
  companyName?: string
  companySiret?: string
}

const props = withDefaults(defineProps<Props>(), {})

const COMMITMENT_MONTHS_FILTER = 36
const TECHNOLOGY_FILTER = 'FTTH'
const OFFER_NAME_FILTER = 'Box Pro'
const BANDWIDTH_FILTER = 1000

const filteredOffers = computed(() => {
  const offers = props.result?.eligibilityOffers ?? []
  return offers.filter(
    (o) =>
      (o.offerName?.includes(OFFER_NAME_FILTER) ?? false) &&
      String(o.commitmentMonths) === String(COMMITMENT_MONTHS_FILTER) &&
      (o.technology?.toUpperCase().includes(TECHNOLOGY_FILTER) ?? false) &&
      Number(o.bandwidthMbps) === BANDWIDTH_FILTER,
  )
})

const formatBandwidth = (mbps: string | number): string => {
  const bandwidth = typeof mbps === 'string' ? parseInt(mbps) : mbps
  if (bandwidth >= 1000) {
    return `${bandwidth / 1000} Gbps`
  }
  return `${bandwidth} Mbps`
}

const eligibleOffersCount = computed(() => {
  return filteredOffers.value.filter((o: EligibilityOffer) => o.status === 'ELIGIBLE').length
})

const eligibleOffers = computed(() => {
  return filteredOffers.value.filter((o: EligibilityOffer) => o.status === 'ELIGIBLE')
})

const notEligibleOffers = computed(() => {
  return filteredOffers.value.filter((o: EligibilityOffer) => o.status === 'NOT_ELIGIBLE')
})

const notEligibleOffersCount = computed(() => notEligibleOffers.value.length)

const activeTab = ref<'disponible' | 'non-disponible'>('disponible')

</script>

<template>
  <div class="space-y-6">
    <!-- Informations entreprise -->
    <div v-if="companyName || companySiret" class="alert alert-default">
      <div class="flex items-center gap-2">
        <BuildingOffice2Icon class="size-5 shrink-0" />
        <div>
          <p v-if="companyName" class="font-semibold">{{ companyName }}</p>
          <p v-if="companySiret" class="text-sm opacity-80">SIRET : {{ companySiret }}</p>
        </div>
      </div>
    </div>

    <!-- Informations générales -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="bg-base-200 p-4 rounded-lg">
        <h3 class="font-semibold mb-2 flex items-center gap-2"><MapPinIcon class="size-5 shrink-0" /> Adresse</h3>
        <p class="text-sm">{{ result?.address || fulladdress }}</p>
        <div v-if="result?.buildingCode" class="mt-2">
          <span class="badge badge-sm badge-outline">{{ result.buildingCode }}</span>
        </div>
      </div>

      <div v-if="result?.gps" class="bg-base-200 p-4 rounded-lg">
        <h3 class="font-semibold mb-2 flex items-center gap-2"><GlobeAltIcon class="size-5 shrink-0" /> Coordonnées GPS</h3>
        <p class="text-sm">Lat: {{ result.gps.latitude }}</p>
        <p class="text-sm">Long: {{ result.gps.longitude }}</p>
      </div>
    </div>
    <!-- Offres d'éligibilité -->
    <div v-if="filteredOffers.length" class="space-y-4">
      <div class="flex items-center gap-1 border-b border-base-300">
        <BriefcaseIcon class="size-5 shrink-0 mr-2" />
        <button
          role="tab"
          class="px-4 py-2 font-medium transition-colors -mb-px border-b-2"
          :class="activeTab === 'disponible' ? 'border-primary text-primary' : 'border-transparent text-base-content/70 hover:text-base-content'"
          @click="activeTab = 'disponible'"
        >
          Offres disponibles ({{ eligibleOffersCount }})
        </button>
        <span class="text-base-content/40">|</span>
        <button
          role="tab"
          class="px-4 py-2 font-medium transition-colors -mb-px border-b-2"
          :class="activeTab === 'non-disponible' ? 'border-primary text-primary' : 'border-transparent text-base-content/70 hover:text-base-content'"
          @click="activeTab = 'non-disponible'"
        >
          Offres non disponibles ({{ notEligibleOffersCount }})
        </button>
      </div>

      <!-- Contenu onglet Disponible -->
      <div v-show="activeTab === 'disponible'" class="space-y-4">
        <p
          v-if="!eligibleOffers.length"
          class="text-base-content/70 text-center py-8 px-6 rounded-2xl bg-base-200 font-semibold"
        >
          Aucune offre n'est disponible
        </p>
        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div
            v-for="(offer, index) in eligibleOffers"
            :key="index"
            class="card bg-base-200 border border-success"
          >
            <div class="card-body p-4">
              <div class="flex items-start justify-between mb-3">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="font-bold text-lg">{{ offer.provider }}</span>
                    <span class="badge badge-sm badge-success inline-flex items-center gap-1"><CheckIcon class="size-3.5" /> Éligible</span>
                  </div>
                  <p class="text-sm text-base-content/70">{{ offer.offerName }}</p>
                </div>
              </div>
              <div class="space-y-2">
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Technologie</span>
                  <span class="badge badge-sm badge-outline">{{ offer.technology }}</span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Débit</span>
                  <span class="font-semibold">{{ formatBandwidth(offer.bandwidthMbps) }}</span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Gamme</span>
                  <span class="text-xs">{{ offer.productFamily }}</span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Engagement</span>
                  <span>{{ offer.commitmentMonths }} mois</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- Contenu onglet Non disponible -->
      <div v-show="activeTab === 'non-disponible'" class="space-y-4">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div
            v-for="(offer, index) in notEligibleOffers"
            :key="index"
            class="card bg-base-200 border border-error opacity-60"
          >
            <div class="card-body p-4">
              <div class="flex items-start justify-between mb-3">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="font-bold text-lg">{{ offer.provider }}</span>
                    <span class="badge badge-sm badge-error inline-flex items-center gap-1"><XMarkIcon class="size-3.5" /> Non éligible</span>
                  </div>
                  <p class="text-sm text-base-content/70">{{ offer.offerName }}</p>
                </div>
              </div>
              <div class="space-y-2">
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Technologie</span>
                  <span class="badge badge-sm badge-outline">{{ offer.technology }}</span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Débit</span>
                  <span class="font-semibold">{{ formatBandwidth(offer.bandwidthMbps) }}</span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Gamme</span>
                  <span class="text-xs">{{ offer.productFamily }}</span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Engagement</span>
                  <span>{{ offer.commitmentMonths }} mois</span>
                </div>
              </div>
              <div v-if="offer.priceEuros" class="divider my-2"></div>
              <div v-if="offer.priceEuros" class="space-y-1">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-base-content/70">Prix mensuel</span>
                  <span class="text-lg font-bold text-primary"> {{ offer.priceEuros.recurrent }}€<span class="text-sm font-normal">/mois</span> </span>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <span class="text-base-content/70">Frais d'accès</span>
                  <span>{{ offer.priceEuros.serviceAccessFees }}€</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
